import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { residualValuesCSS } from '../ResidualValues.css';
import { getStatePlates, updateStatePlates } from '../../functions/functionsFetch';
import { getZipCode } from '../../../Cotizador/Steps/Step1/getZipCode';

const fields = [
    { key: 'default_zip', label: 'CP', placeholder: '03710', numeric: true },
    { key: 'default_colony', label: 'Colonia', placeholder: 'CIUDAD DE LOS DEPORTES' },
    { key: 'default_municipality', label: 'Municipio', placeholder: 'BENITO JUAREZ' },
    { key: 'default_city', label: 'Ciudad', placeholder: 'CIUDAD DE MEXICO' },
    { key: 'default_state', label: 'Estado', placeholder: 'CIUDAD DE MEXICO' },
];

export const StatePlateDefaults = ({ toast, logout }) => {
    const [statePlates, setStatePlates] = useState([]);
    const [loading, setLoading] = useState({
        fetch: false,
        save: false,
    });
    const [zipLookupById, setZipLookupById] = useState({});

    useEffect(() => {
        let isMounted = true;

        const loadStatePlates = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            const result = await getStatePlates(logout);

            if (isMounted && Array.isArray(result)) {
                setStatePlates(result);
            }

            if (isMounted) {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        loadStatePlates();

        return () => {
            isMounted = false;
        };
    }, [logout]);

    const handleChange = (id, field, value, numeric = false) => {
        const nextValue = numeric
            ? value.replace(/\D/g, '').slice(0, 5)
            : value.toUpperCase();

        setStatePlates(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, [field]: nextValue }
                    : item
            )
        );
    };

    const normalizeText = (value) => {
        if (value === null || value === undefined) {
            return '';
        }

        return value
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();
    };

    const autofillAddressFromZip = async (id, zip) => {
        if (!zip || zip.length !== 5) {
            return;
        }

        setZipLookupById(prev => ({ ...prev, [id]: true }));

        try {
            const response = await getZipCode(zip);

            if (!response || response.status !== 200 || !response.data?.length) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'CP no encontrado',
                    detail: `No se encontró información para el CP ${zip}.`,
                });
                return;
            }

            const firstMatch = response.data[0];
            const normalizedState = normalizeText(firstMatch.estado === 'CDMX' ? 'CIUDAD DE MEXICO' : firstMatch.estado);
            const normalizedMunicipality = normalizeText(firstMatch.municipio);
            const normalizedColony = normalizeText(firstMatch.colonia);

            setStatePlates(prev =>
                prev.map(item =>
                    item.id === id
                        ? {
                            ...item,
                            default_zip: zip,
                            default_colony: normalizedColony,
                            default_municipality: normalizedMunicipality,
                            default_city: normalizedState,
                            default_state: normalizedState,
                        }
                        : item
                )
            );

            toast.current.show({
                severity: 'success',
                summary: 'Dirección encontrada',
                detail: `Se autocompletó la dirección para el CP ${zip}.`,
            });
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: `No se pudo consultar el CP ${zip}.`,
            });
            console.error('Error autofilling address from zip:', error);
        } finally {
            setZipLookupById(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(prev => ({ ...prev, save: true }));

        const updates = statePlates.map(item => ({
            id: item.id,
            default_zip: item.default_zip?.trim() || null,
            default_colony: item.default_colony?.trim() || null,
            default_municipality: item.default_municipality?.trim() || null,
            default_city: item.default_city?.trim() || null,
            default_state: item.default_state?.trim() || null,
        }));

        try {
            const result = await updateStatePlates(updates);

            if (result?.data?.status === 200) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Ok',
                    detail: 'Direcciones por placa actualizadas',
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron actualizar las direcciones por placa',
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron actualizar las direcciones por placa',
            });
            console.error('Error updating state plates:', error);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };

    return (
        <section css={residualValuesCSS()}>
            <form onSubmit={handleSubmit}>
                <div
                    style={{
                        width: 'min(1120px, 90%)',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.5rem',
                    }}
                >
                    <div>
                        <h2 className='text-2xl font-bold m-0'>Dirección por estado de placa</h2>
                        <p className='mt-2 mb-0' style={{ color: '#5d6b76', lineHeight: 1.5 }}>
                            Estos valores se aplican en el cotizador para ocultar el domicilio manual y cargar el CP base segun la placa.
                        </p>
                    </div>

                    {statePlates.map(item => (
                        <section
                            key={item.id}
                            style={{
                                border: '1px solid #d8e7ee',
                                borderRadius: '24px',
                                padding: '1.5rem',
                                background: '#ffffff',
                                boxShadow: '0 10px 24px rgba(33, 88, 140, 0.06)',
                            }}
                        >
                            <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-4'>
                                <div>
                                    <h3 className='m-0' style={{ color: '#21588C' }}>{item.state}</h3>
                                    <span style={{ color: '#6d7f8b', fontSize: '0.95rem' }}>
                                        Configura el domicilio default para seguros y cotizacion.
                                    </span>
                                </div>
                            </div>

                            <div className='grid'>
                                {fields.map(field => (
                                    <div className='col-12 md:col-6 xl:col' key={field.key}>
                                        <label
                                            htmlFor={`${field.key}-${item.id}`}
                                            className='block mb-2 font-semibold'
                                            style={{ color: '#35556d' }}
                                        >
                                            {field.label}
                                        </label>
                                        <InputText
                                            id={`${field.key}-${item.id}`}
                                            value={item[field.key] || ''}
                                            placeholder={field.placeholder}
                                            className='TextInput'
                                            onChange={(event) => {
                                                const nextValue = field.numeric
                                                    ? event.target.value.replace(/\D/g, '').slice(0, 5)
                                                    : event.target.value;

                                                handleChange(item.id, field.key, nextValue, field.numeric);

                                                if (field.key === 'default_zip' && nextValue.length === 5) {
                                                    autofillAddressFromZip(item.id, nextValue);
                                                }
                                            }}
                                        />
                                        {field.key === 'default_zip' && zipLookupById[item.id] && (
                                            <small style={{ color: '#5d6b76', display: 'block', marginTop: '0.45rem' }}>
                                                Buscando dirección...
                                            </small>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}

                    <div className="ButtonWithShadow" style={{ alignSelf: 'center' }}>
                        <Button
                            label="Guardar direcciones"
                            icon="pi pi-check"
                            loading={loading.save}
                            disabled={loading.fetch || statePlates.length === 0}
                            className='Green py-3 mt-4 W-4/5'
                            type='submit'
                        />
                        <div className="Shadow"></div>
                    </div>
                </div>
            </form>
        </section>
    );
};
